
import Vue, { PropType } from 'vue';
import { db, Query, QuerySnapshot, QueryDocumentSnapshot } from '@/firebase';
import pluralize from 'pluralize';
import _mapKeys from 'lodash/mapKeys';
import { TeamMember } from '@run-diffusion/shared';
import { mapState, mapGetters } from 'vuex';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import DebouncedInput from '@/components/base/DebouncedInput.vue';

export default Vue.extend({
	name: 'TeamMembersPicker',
	props: {
		/**
		 * Mirrors what `select` emits: a single `TeamMember` (or null) under
		 * `singleSelect`, an array otherwise.
		 */
		selected: {
			type: [Array, Object] as PropType<TeamMember[] | TeamMember | null>,
			default: null,
		},
		/**
		 * One member at a time. Picking replaces the selection and closes the
		 * picker, and the rows show radios rather than checkboxes so the control
		 * does not promise a multi-select it will not honour.
		 *
		 * Off by default: every existing caller picks several members.
		 */
		singleSelect: {
			type: Boolean,
			default: false,
		},
	},
	data () {
		return {
			localIsOpen: false,
			loadingTeamMembers: false,
			teamMembers: [] as TeamMember[],
			localSelectedMap: {} as Record<string, TeamMember>,
			searchQuery: '',
			hasPendingSearch: false,
		};
	},
	computed: {
		...mapState([
			'team',
		]),
		...mapGetters([
			'displayLabels',
		]),
		/**
		 * Reached only when a load returned nothing and nothing is typed, so the
		 * team genuinely has no members to pick. Names the team, since the picker
		 * only ever loads the selected one and "no members" invites the reader to
		 * assume it spans the company.
		 */
		emptyStateText (): string {
			const teamName: string = this._get(this.team, 'name') || '';
			const base: string = `No ${this.displayLabels.team} members to show`;
			return teamName ? `${base} in ${teamName}` : base;
		},
		shouldShowResults (): boolean {
			return !!this.teamMembers.length || !!this.searchQuery.length;
		},
		searchResultsText (): string | null {
			if (this.teamMembers.length > 0) {
				return `${this.teamMembers.length} ${pluralize('member', this.teamMembers.length)} found`;
			}

			return null;
		},
		selectedCount (): number {
			return Object.keys(this.localSelectedMap).length;
		},
		/** The chosen member's email in single-select mode, else null. */
		selectedEmail (): string | null {
			// Asserted because `this` typing inside a computed loses the map's value
			// type, unlike in a method.
			const [first] = Object.values(this.localSelectedMap) as TeamMember[];
			return this._get(first, 'email') || null;
		},
		/** Either prop shape as one list, so the internal map is built one way. */
		selectedAsList (): TeamMember[] {
			if (!this.selected) return [];
			return Array.isArray(this.selected) ? this.selected : [this.selected];
		},
	},
	watch: {
		localIsOpen: {
			immediate: true,
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal && newVal !== oldVal) {
					// Reset state when menu opens
					this.teamMembers = [];
					this.searchQuery = '';
					// Unprompted first page. `searchQuery` was already '' on a
					// second open, so its watcher would not fire.
					this.loadTeamMembers();
					// Convert to map
					const selectedMap: Record<string, TeamMember> = _mapKeys(this.selectedAsList, 'id');
					this.localSelectedMap = selectedMap;

					// Focus search bar after menu opens
					setTimeout(() => {
						this.$refs.searchDebouncedInput?.focus();
					}, 500);
				} else if (!newVal && oldVal) {
					// Menu is closing, emit the selected members
					this.onSubmit();
				}
			},
		},
		selected: {
			immediate: true,
			handler (newVal: TeamMember[] | TeamMember | null, oldVal: TeamMember[] | TeamMember | null) {
				if (newVal !== oldVal) {
					// Convert to map
					const selectedMap: Record<string, TeamMember> = _mapKeys(this.selectedAsList, 'id');
					this.localSelectedMap = selectedMap;
				}
			},
		},
		searchQuery: {
			handler (newVal: string, oldVal: string) {
				// Every change re-searches, clearing back to empty included: that
				// returns the first page rather than an empty panel.
				if (newVal !== oldVal) {
					this.loadTeamMembers();
				}
			},
		},
	},
	methods: {
		async loadTeamMembers () {
			const searchQuery: string = this.searchQuery.toLowerCase();
			if (this.loadingTeamMembers) {
				// Store this search so that we can fire it off after the loading is complete
				this.hasPendingSearch = true;
				return;
			}

			// No minimum length: an empty query is `email >= ''`, which matches every
			// member, so opening the picker shows a first page instead of a blank
			// panel that reads as broken. A 1-character query is a real prefix
			// search and is allowed for the same reason.
			if (!this.team?.id || this.loadingTeamMembers) {
				return;
			}

			this.loadingTeamMembers = true;
			try {
				const teamMembersRef: Query = db
					.collection('teams')
					.doc(this.team.id)
					.collection('teamMembers')
					.where('isAccepted', '==', true)
					.where('email', '>=', searchQuery)
					.orderBy('email', 'asc')
					.limit(50);

				const snapshot: QuerySnapshot = await teamMembersRef.get();
				const members: TeamMember[] = [];
				snapshot.forEach((doc: QueryDocumentSnapshot) => {
					members.push({
						...doc.data(),
						id: doc.id,
					} as TeamMember);
				});

				this.teamMembers = members;
			} catch (error) {
				console.error('Error loading team members:', error);
			} finally {
				this.loadingTeamMembers = false;
				if (this.hasPendingSearch) {
					this.hasPendingSearch = false;
					this.loadTeamMembers();
				}
			}
		},
		setSearchQuery (query: string) {
			this.searchQuery = query || '';
		},
		isSelected (member: TeamMember): boolean {
			return !!this.localSelectedMap[member.id];
		},
		toggleMember (member: TeamMember) {
			const wasSelected: boolean = this.isSelected(member);

			if (this.singleSelect) {
				// Replace rather than accumulate. Re-picking the current one clears
				// it, which is the only way to get back to "none" without a button.
				this.localSelectedMap = wasSelected ? {} : { [member.id]: member };
				// Closing is what emits (see the isOpen watcher), so a pick needs no
				// second confirming click.
				if (!wasSelected) this.localIsOpen = false;
				return;
			}

			const newSelected: Record<string, TeamMember> = { ...this.localSelectedMap };
			if (wasSelected) {
				// Remove from selection
				delete newSelected[member.id];
			} else {
				// Add to selection
				newSelected[member.id] = member;
			}
			this.localSelectedMap = newSelected;
		},
		onSubmit () {
			const selectedMembers: TeamMember[] = Object.values(this.localSelectedMap);
			// A single-select caller can only hold one member, so hand it the member
			// rather than a one-item array it would have to unwrap.
			this.$emit('select', this.singleSelect ? selectedMembers[0] || null : selectedMembers);
			this.localIsOpen = false;
		},
		onClearSelection () {
			this.localSelectedMap = {};
		},
	},
	components: {
		MenuOrBottomSheet,
		DebouncedInput,
	},
});
